import { MouseEvent, useState } from 'react';
import { ListSubheader, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { CourseMappingPreset } from './courseMappingPresets';

import './CourseMappingViewSelector.css';

interface CourseMappingViewSelectorProps {
  presets: CourseMappingPreset[];
  selectedPresetId: string;
  unviewedPresetIds: Set<string>;
  onSelectPreset: (presetId: string) => void;
}

const CourseMappingViewSelector = ({
  presets,
  selectedPresetId,
  unviewedPresetIds,
  onSelectPreset,
}: CourseMappingViewSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const activePreset = presets.find((preset) => preset.id === selectedPresetId) ?? presets[0];
  const showHeaderBeacon = !isOpen && unviewedPresetIds.size > 0;

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (presetId: string) => {
    onSelectPreset(presetId);
    handleClose();
  };

  return (
    <>
      <button
        type="button"
        className={`course-mapping-view-selector-trigger${isOpen ? ' course-mapping-view-selector-trigger--open' : ''}`}
        aria-label="Select a predefined filter"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={handleOpen}
      >
        <span className="course-mapping-view-title">{activePreset.title}</span>
        <ArrowDropDownIcon className="course-mapping-view-selector-icon" fontSize="small" />
        {showHeaderBeacon && (
          <span
            className="course-mapping-view-selector-header-beacon course-mapping-view-selector-header-beacon--pulsing"
            data-testid="course-mapping-header-beacon"
            aria-hidden="true"
          />
        )}
      </button>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        className="course-mapping-view-selector-menu"
        PaperProps={{
          className: 'course-mapping-view-selector-menu-paper',
        }}
        MenuListProps={{
          'aria-label': 'Predefined filters',
        }}
      >
        <ListSubheader className="course-mapping-view-selector-menu-header" disableSticky>
          Select a view
        </ListSubheader>
        {presets.map((preset) => {
          const isSelected = preset.id === activePreset.id;
          const showBeacon = unviewedPresetIds.has(preset.id);

          return (
            <MenuItem
              key={preset.id}
              className={`course-mapping-view-selector-menu-item${
                isSelected ? ' course-mapping-view-selector-menu-item--selected' : ''
              }`}
              selected={isSelected}
              aria-selected={isSelected}
              onClick={() => handleSelect(preset.id)}
            >
              <span className="course-mapping-view-selector-menu-item-title">{preset.title}</span>
              {showBeacon && <span className="course-mapping-view-selector-beacon" aria-label="Not visited yet" />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default CourseMappingViewSelector;
